/**
 * Utility for validating store URLs
 */

// List of reserved routes that cannot be used as store URLs
const RESERVED_ROUTES = [
  // App pages
  'login',
  'register',
  'forgot-password',
  'onboarding',
  'dashboard',
  'admin',
  
  // Landing pages
  'features',
  'product',
  'pricing',
  'templates',
  'blog',
  'contact',
  'about',
  'enterprise',
  'company',
  'solutions',
  'careers',
  'team',
  'compliance',
  'merchant-agreement',
  
  // Legal pages
  'privacy',
  'terms',
  'cookies',

  // System reserved
  'api',
  'auth',
  'cdn',
  'app',
  'www',
  'help',
  'support',
  'shop',
  'store',
  'static',
  'media',
  'assets',
  'images',
  'img',
  'css',
  'js',
  'docs',
  'documentation',
  'settings',
  'account',
  'profile',
  'payment',
  'checkout',
  'cart',
  
  // Route patterns
  'dashboard-*',
  'api-*',
  'admin-*',
];

/**
 * Check if a URL is valid for use as a store URL
 * @param url The URL to validate
 * @returns An object with validation result and error message
 */
export function validateStoreUrl(url: string): { valid: boolean; message?: string } {
  // Check for empty URL
  if (!url || url.trim() === '') {
    return { valid: false, message: 'URL cannot be empty' };
  }

  // Check for minimum length
  if (url.length < 3) {
    return { valid: false, message: 'URL must be at least 3 characters' };
  }

  // Check for valid characters (letters, numbers, hyphens)
  if (!/^[a-z0-9-]+$/.test(url)) {
    return { 
      valid: false, 
      message: 'URL can only contain lowercase letters, numbers, and hyphens' 
    };
  }

  // Check if URL starts or ends with hyphen
  if (url.startsWith('-') || url.endsWith('-')) {
    return { 
      valid: false, 
      message: 'URL cannot start or end with a hyphen' 
    };
  }

  // Check for reserved routes
  for (const route of RESERVED_ROUTES) {
    if (route.endsWith('*')) {
      // For wildcard routes, check if URL starts with the pattern
      const pattern = route.slice(0, -1);
      if (url.startsWith(pattern)) {
        return { 
          valid: false, 
          message: `This URL is reserved and cannot be used. Please choose a different URL.` 
        };
      }
    } else if (url === route) {
      return { 
        valid: false, 
        message: `This URL is reserved and cannot be used. Please choose a different URL.` 
      };
    }
  }

  return { valid: true };
}

export const CLAIMED_STORE_URL_KEY = "claimedStoreUrl";
export const PENDING_BUSINESS_DATA_KEY = "businessData";

/** Input sanitizer: lowercase letters, numbers, and hyphens. */
export function normalizeStoreHandle(value: string): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
}

/** Returns a handle that passes store URL rules, or an empty string. */
export function getValidStoreHandle(value: string): string {
  const url = normalizeStoreHandle(value).replace(/^-+|-+$/g, "");
  return validateStoreUrl(url).valid ? url : "";
}

function readSession(key: string): string {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeSession(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore quota / private-mode failures; signup can still continue.
  }
}

function removeSession(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

export function persistClaimedStoreUrl(handle: string): string {
  const url = getValidStoreHandle(handle);
  if (!url) {
    removeSession(CLAIMED_STORE_URL_KEY);
    return "";
  }
  writeSession(CLAIMED_STORE_URL_KEY, url);
  return url;
}

export function readClaimedStoreUrl(): string {
  return getValidStoreHandle(readSession(CLAIMED_STORE_URL_KEY));
}

export function clearClaimedStoreUrl() {
  removeSession(CLAIMED_STORE_URL_KEY);
}

type PendingBusinessData = {
  name?: string;
  whatsappNumber?: string;
  url?: string;
};

export function readPendingBusinessData(): PendingBusinessData {
  try {
    const raw = readSession(PENDING_BUSINESS_DATA_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PendingBusinessData;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function writePendingBusinessData(data: PendingBusinessData) {
  const existing = readPendingBusinessData();
  writeSession(
    PENDING_BUSINESS_DATA_KEY,
    JSON.stringify({ ...existing, ...data }),
  );
}

export function clearPendingBusinessData() {
  removeSession(PENDING_BUSINESS_DATA_KEY);
} 